import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "../lib/api";
import Layout from "../components/Layout";
import { MessageCircle, Send, RefreshCw, Paperclip, Zap, Trash2, ListOrdered, MessageSquarePlus, Search, X, Bot, Hand, Reply } from "lucide-react";
import { toast } from "sonner";
import WaQueuePanel from "./WaQueuePanel";

// Modal "Chat Baru": cari pelanggan, nomor WA diambil dari data pelanggan.
function NewChatModal({ onClose, onPick, line = 1 }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [checking, setChecking] = useState("");

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/wa/contacts", { params: { q: term } });
        setResults(data || []);
      } catch (_) {
        setResults([]);
      }
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pick = async (c) => {
    if (checking) return;
    setChecking(c.id);
    try {
      const { data } = await api.get(`/wa/check/${c.wa_phone}`, { params: { line } });
      if (data.exists === false) {
        toast.error(`Nomor +${c.wa_phone} (${c.name}) tidak terdaftar di WhatsApp`, { duration: 7000 });
        return;
      }
      onPick({ chat_id: data.chat_id || c.chat_id, phone: c.wa_phone, name: c.name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengecek nomor");
    } finally {
      setChecking("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 pt-[10vh]" onClick={onClose}>
      <div className="bg-white w-full max-w-md border border-[#E4E4E7] shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="wa-newchat-modal">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between">
          <div className="font-black text-sm uppercase tracking-tight flex items-center gap-2"><MessageSquarePlus size={16} className="text-[#16A34A]" /> Chat Baru</div>
          <button onClick={onClose} className="text-[#52525B] hover:text-black"><X size={16} /></button>
        </div>
        <div className="p-3 border-b border-[#E4E4E7] flex items-center gap-2">
          <Search size={14} className="text-[#A1A1AA]" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama / no WA / alamat pelanggan…"
            className="flex-1 outline-none text-sm"
            data-testid="wa-newchat-search"
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="p-5 text-center text-xs text-[#52525B]">Ketik minimal 2 huruf untuk mencari pelanggan.</div>
          ) : searching ? (
            <div className="p-5 text-center text-xs text-[#52525B]">Mencari…</div>
          ) : results.length === 0 ? (
            <div className="p-5 text-center text-xs text-[#52525B]">Pelanggan tidak ditemukan / belum ada no WA.</div>
          ) : results.map((c) => (
            <button key={c.id} onClick={() => pick(c)} disabled={!!checking} data-testid={`wa-newchat-item-${c.wa_phone}`}
              className="w-full text-left px-4 py-2.5 border-b border-[#E4E4E7]/60 hover:bg-[#F4F4F5] disabled:opacity-60">
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold text-sm truncate">{c.name}</div>
                <div className="text-[10px] text-[#A1A1AA] font-mono whitespace-nowrap">{checking === c.id ? "Mengecek…" : `+${c.wa_phone}`}</div>
              </div>
              {(c.address || c.city) && <div className="text-[11px] text-[#52525B] truncate">{[c.address, c.city].filter(Boolean).join(", ")}</div>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const fmtTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  return same
    ? d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
};

// /whatsapp = WA 1 (link lama tetap jalan), /whatsapp/2, /whatsapp/3 = nomor Tencent.
export default function WhatsAppInbox() {
  const { line: lineParam } = useParams();
  const n = parseInt(lineParam || "1", 10);
  const line = [1, 2, 3].includes(n) ? n : 1;
  return <Inbox key={line} line={line} />;
}

function Inbox({ line }) {
  const lp = { params: { line } };
  const [lineInfo, setLineInfo] = useState(null); // {label, enabled, me}
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null); // chat_id
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [delChat, setDelChat] = useState(null); // {chat_id, title, alsoWa, busy}
  const [replyTo, setReplyTo] = useState(null); // {id, body, from_me} pesan yang sedang dibalas
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [sessionStatus, setSessionStatus] = useState("");
  const [shortcuts, setShortcuts] = useState([]);
  const [scOpen, setScOpen] = useState(false);
  const [scQuery, setScQuery] = useState("");
  const [queueOpen, setQueueOpen] = useState(false);
  const [queuePending, setQueuePending] = useState(0);
  const [aiGlobal, setAiGlobal] = useState(null); // {enabled, switch_on, has_key}
  const [newChatOpen, setNewChatOpen] = useState(false);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadChats = async () => {
    try {
      const { data } = await api.get("/wa/chats", lp);
      setChats(data);
    } catch (_) { /* poll berikutnya */ }
    setLoading(false);
  };

  const loadThread = async (chatId, scroll = true) => {
    try {
      const { data } = await api.get(`/wa/chats/${encodeURIComponent(chatId)}/messages`, lp);
      if (activeRef.current === chatId) {
        setThread(data);
        if (scroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }), 50);
      }
    } catch (_) { /* poll berikutnya */ }
  };

  const loadSessionStatus = async () => {
    try {
      const { data } = await api.get("/wa/session/status", lp);
      setSessionStatus(data.status || "");
    } catch (_) {}
    try {
      const { data } = await api.get("/wa/lines");
      setLineInfo((data || []).find((x) => x.line === line) || null);
    } catch (_) {}
    if (line === 1) {
      try {
        const { data } = await api.get("/wa/queue");
        setQueuePending(data?.stats?.pending || 0);
      } catch (_) {}
    }
    try {
      const { data } = await api.get("/wa/ai/status");
      setAiGlobal(data);
    } catch (_) {}
  };

  useEffect(() => {
    loadChats();
    loadSessionStatus();
    // link dari notifikasi WA admin: /whatsapp?chat=628xxx@c.us → langsung buka chat itu
    const qChat = new URLSearchParams(window.location.search).get("chat");
    if (qChat) openChat(qChat);
    api.get("/wa/shortcuts").then((r) => setShortcuts(r.data || [])).catch(() => {});
    const iv = setInterval(() => {
      loadChats();
      if (activeRef.current) loadThread(activeRef.current, false);
    }, 8000);
    const ivSession = setInterval(loadSessionStatus, 30000);
    return () => { clearInterval(iv); clearInterval(ivSession); };
  }, []);

  const startReply = (m) => {
    if (!m.wa_id) { toast.info("Pesan ini belum tersinkron dari WhatsApp, coba sebentar lagi."); return; }
    setReplyTo({ id: m.id, body: m.body || (m.media_file ? "📎 [media]" : ""), from_me: m.from_me });
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const jumpToMessage = (key) => {
    const el = key && document.getElementById(`wamsg-${key}`);
    if (!el) { toast.info("Pesan asli tidak ada di riwayat yang dimuat."); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("bg-[#FEF9C3]");
    setTimeout(() => el.classList.remove("bg-[#FEF9C3]"), 1500);
  };

  const openChat = (chatId) => {
    setReplyTo(null);
    setActive(chatId);
    setThread(null);
    loadThread(chatId);
    setChats((p) => p.map((c) => (c.chat_id === chatId ? { ...c, unread: 0 } : c)));
  };

  const startNewChat = ({ chat_id, phone, name }) => {
    setNewChatOpen(false);
    setReplyTo(null);
    const existing = chats.find((c) => c.chat_id === chat_id || c.phone === phone);
    if (existing) { openChat(existing.chat_id); return; }
    // Chat belum ada di inbox: buka thread kosong, akan muncul di daftar setelah pesan pertama terkirim
    activeRef.current = chat_id;
    setActive(chat_id);
    setThread({ chat_id, phone, name, messages: [] });
    loadThread(chat_id);
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !active || sending) return;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/wa/chats/${encodeURIComponent(active)}/send`, { text: t, reply_to: replyTo?.id || "" }, lp);
      setText("");
      setReplyTo(null);
      setThread((p) => (p ? { ...p, messages: [...p.messages, msg] } : p));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      loadChats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim pesan", { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  const fileRef = useRef(null);

  const deleteChat = async () => {
    if (!delChat || delChat.busy) return;
    setDelChat((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.delete(`/wa/chats/${encodeURIComponent(delChat.chat_id)}`, { params: { also_whatsapp: delChat.alsoWa, line } });
      if (delChat.alsoWa && !data.wa_deleted) toast.warning(`Dihapus dari inbox (${data.deleted} pesan). Di WhatsApp gagal: ${data.wa_error}`, { duration: 9000 });
      else toast.success(`Percakapan dihapus (${data.deleted} pesan)${data.wa_deleted ? " — juga di WhatsApp" : ""}`);
      if (activeRef.current === delChat.chat_id) { setActive(null); setThread(null); setReplyTo(null); }
      setChats((p) => p.filter((c) => c.chat_id !== delChat.chat_id));
      setDelChat(null);
      loadChats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus percakapan");
      setDelChat((d) => (d ? { ...d, busy: false } : d));
    }
  };

  const deleteMessage = async (m) => {
    if (!window.confirm(m.from_me ? "Hapus pesan ini? Pesan juga akan dihapus di WhatsApp penerima (jika masih memungkinkan)." : "Hapus pesan ini dari inbox?")) return;
    try {
      const { data } = await api.delete(`/wa/chats/${encodeURIComponent(active)}/messages/${m.id}`, lp);
      setThread((p) => (p ? { ...p, messages: p.messages.filter((x) => x.id !== m.id) } : p));
      if (data.wa_deleted) toast.success("Pesan dihapus dari WhatsApp ✓");
      else if (data.wa_error) toast.warning(`Dihapus dari inbox. ${data.wa_error}`, { duration: 7000 });
      else toast.success("Pesan dihapus dari inbox");
      loadChats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus pesan");
    }
  };

  const sendMedia = async (file) => {
    if (!file || !active || sending) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Ukuran file maksimal 15 MB"); return; }
    setSending(true);
    const tid = toast.loading(`Mengirim ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", text.trim());
      fd.append("reply_to", replyTo?.id || "");
      const { data: msg } = await api.post(`/wa/chats/${encodeURIComponent(active)}/send-media`, fd, { headers: { "Content-Type": "multipart/form-data" }, params: { line } });
      setText("");
      setReplyTo(null);
      setThread((p) => (p ? { ...p, messages: [...p.messages, msg] } : p));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      toast.success("Media terkirim via WAHA ✓", { id: tid });
      loadChats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim media", { id: tid, duration: 8000 });
    } finally {
      setSending(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setAiMode = async (mode) => {
    if (!active) return;
    try {
      await api.post(`/wa/ai/state/${encodeURIComponent(active)}`, { mode }, lp);
      setChats((p) => p.map((c) => (c.chat_id === active ? { ...c, ai_mode: mode, needs_admin: false, ai_paused: false } : c)));
      toast.success(mode === "auto" ? "Chat diserahkan ke AI" : "Chat diambil alih admin — AI berhenti membalas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah mode AI");
    }
  };

  const activeChat = chats.find((c) => c.chat_id === active);
  const aiMode = activeChat?.ai_mode || "auto";
  const title = thread?.name || activeChat?.name || thread?.phone || activeChat?.phone || "";

  return (
    <Layout>
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2">
            <MessageCircle className="text-[#16A34A]" size={28} /> WHATSAPP
            <span className="text-base sm:text-lg md:text-xl font-black px-2 py-0.5 bg-[#16A34A] text-white" data-testid="wa-line-badge">{lineInfo?.label || `WA ${line}`}</span>
          </h1>
          <p className="text-xs text-[#52525B] mt-1">
            {line === 1
              ? "Nomor utama (sistem, bot & pelanggan). Pesan masuk & keluar via WAHA — diperbarui otomatis setiap 8 detik."
              : "Nomor khusus pelanggan — dijawab AI Agent yang sama. Diperbarui otomatis setiap 8 detik."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button data-testid="wa-newchat-open" onClick={() => setNewChatOpen(true)} className="px-3 py-2 bg-[#16A34A] text-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#15803D]">
            <MessageSquarePlus size={13} /> Chat Baru
          </button>
          {line === 1 && (
            <button data-testid="wa-queue-open" onClick={() => setQueueOpen(true)} className="px-3 py-2 border border-[#E4E4E7] bg-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#F4F4F5] relative">
              <ListOrdered size={13} /> Antrian
              {queuePending > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#D97706] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full" data-testid="wa-queue-badge">{queuePending}</span>}
            </button>
          )}
          <button data-testid="wa-inbox-refresh" onClick={() => { loadChats(); if (active) loadThread(active, false); }} className="px-3 py-2 border border-[#E4E4E7] bg-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#F4F4F5]">
            <RefreshCw size={13} /> Muat Ulang
          </button>
        </div>
      </div>

      {queueOpen && <WaQueuePanel onClose={() => setQueueOpen(false)} />}
      {newChatOpen && <NewChatModal line={line} onClose={() => setNewChatOpen(false)} onPick={startNewChat} />}

      {delChat && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !delChat.busy && setDelChat(null)}>
          <div className="bg-white border border-[#E4E4E7] w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="wa-chat-delete-modal">
            <div className="font-bold text-sm mb-2 flex items-center gap-2 text-[#DC2626]"><Trash2 size={15} /> Hapus percakapan?</div>
            <div className="text-xs text-[#52525B] mb-3">
              Semua pesan &amp; media dengan <b>{delChat.title || "kontak ini"}</b> akan dihapus dari inbox aplikasi dan tidak bisa dikembalikan.
              Pesan baru dari kontak ini tetap akan masuk seperti biasa.
            </div>
            <label className="flex items-start gap-2 text-xs mb-4 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={delChat.alsoWa} disabled={delChat.busy}
                onChange={(e) => setDelChat((d) => ({ ...d, alsoWa: e.target.checked }))} data-testid="wa-chat-delete-also-wa" />
              <span>Hapus juga chat ini di WhatsApp nomor perusahaan (HP / WA Web). <span className="text-[#A1A1AA]">Tidak menghapus di HP pelanggan.</span></span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDelChat(null)} disabled={delChat.busy} className="px-3 py-2 border border-[#E4E4E7] text-xs font-bold uppercase hover:bg-[#F4F4F5] disabled:opacity-50">Batal</button>
              <button onClick={deleteChat} disabled={delChat.busy} data-testid="wa-chat-delete-confirm" className="px-3 py-2 bg-[#DC2626] text-white text-xs font-bold uppercase hover:bg-[#B91C1C] disabled:opacity-50">
                {delChat.busy ? "Menghapus…" : "Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}

      {line !== 1 && lineInfo && !lineInfo.enabled ? (
        <div className="mb-3 px-4 py-3 border border-[#D97706]/30 bg-[#D97706]/5 text-[#D97706] text-xs font-bold" data-testid="wa-line-inactive">
          ⚠ WA {line} belum diaktifkan — owner dapat mengaktifkannya di Pengaturan → WhatsApp Gateway → Nomor WhatsApp.
        </div>
      ) : sessionStatus && sessionStatus !== "WORKING" && (
        <div className="mb-3 px-4 py-3 border border-[#DC2626]/30 bg-[#DC2626]/5 text-[#DC2626] text-xs font-bold flex items-center gap-2 flex-wrap" data-testid="wa-session-warning">
          <span>⚠ Sesi WA {line} belum terhubung (status: {sessionStatus}) — pesan masuk TIDAK akan terupdate. Owner perlu scan QR di Pengaturan → WhatsApp Gateway{line === 1 ? "" : " → Nomor WhatsApp"}.</span>
          <a href="/settings" className="underline">Buka Pengaturan</a>
        </div>
      )}

      <div className="bg-white border border-[#E4E4E7] flex" style={{ height: "calc(100vh - 210px)", minHeight: 420 }}>
        {/* Daftar chat */}
        <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-[320px] md:min-w-[320px] border-r border-[#E4E4E7] flex-col`}>
          <div className="px-4 py-3 border-b border-[#E4E4E7] label-cap text-[10px]">Percakapan ({chats.length})</div>
          <div className="flex-1 overflow-y-auto" data-testid="wa-chat-list">
            {loading ? (
              <div className="p-6 text-center text-xs text-[#52525B]">Memuat…</div>
            ) : chats.length === 0 ? (
              <div className="p-6 text-center text-xs text-[#52525B]" data-testid="wa-chat-empty">
                Belum ada pesan. Pesan yang terkirim / diterima via WAHA akan muncul di sini otomatis.
              </div>
            ) : chats.map((c) => (
              <button key={c.chat_id} data-testid={`wa-chat-item-${c.phone}`} onClick={() => openChat(c.chat_id)}
                className={`w-full text-left px-4 py-3 border-b border-[#E4E4E7]/60 hover:bg-[#F4F4F5] ${active === c.chat_id ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-sm truncate">{c.name || `+${c.phone}`}</div>
                  <div className="text-[10px] text-[#A1A1AA] font-mono whitespace-nowrap">{fmtTime(c.last_ts)}</div>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <div className="text-xs text-[#52525B] truncate">{c.last_from_me ? "Anda: " : ""}{c.last_body}</div>
                  {c.unread > 0 && <span data-testid={`wa-chat-unread-${c.phone}`} className="bg-[#16A34A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{c.unread}</span>}
                </div>
                {c.name && <div className="text-[10px] text-[#A1A1AA] font-mono">+{c.phone}</div>}
                {c.needs_admin ? (
                  <div className="mt-1 inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 bg-[#D97706] text-white" data-testid={`wa-chat-needs-admin-${c.phone}`}>⚑ Butuh Admin</div>
                ) : c.ai_mode === "manual" ? (
                  <div className="mt-1 inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 border border-[#A1A1AA] text-[#52525B]">Manual</div>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#A1A1AA]">Pilih percakapan di kiri</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
                <button className="md:hidden text-xs font-bold text-[#002FA7] mr-1" onClick={() => setActive(null)} data-testid="wa-thread-back">←</button>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm" data-testid="wa-thread-title">{title}</div>
                  <div className="text-[10px] text-[#A1A1AA] font-mono">+{thread?.phone || activeChat?.phone}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDelChat({ chat_id: active, title, alsoWa: false, busy: false })}
                    data-testid="wa-chat-delete"
                    title="Hapus percakapan ini"
                    className="p-1.5 border border-[#E4E4E7] bg-white text-[#DC2626] hover:bg-[#DC2626]/10 hover:border-[#DC2626]/30"
                  >
                    <Trash2 size={13} />
                  </button>
                  <span className={`hidden sm:inline text-[10px] font-bold uppercase ${activeChat?.needs_admin ? "text-[#D97706]" : aiMode === "manual" ? "text-[#52525B]" : (aiGlobal && !aiGlobal.enabled) || activeChat?.ai_paused ? "text-[#A1A1AA]" : "text-[#16A34A]"}`} data-testid="wa-ai-status">
                    {activeChat?.needs_admin ? "⚑ Butuh admin" : aiMode === "manual" ? "Ditangani admin" : aiGlobal && !aiGlobal.enabled ? "AI Agent nonaktif" : activeChat?.ai_paused ? "AI jeda (admin baru balas)" : "AI aktif"}
                  </span>
                  {aiMode === "manual" ? (
                    <button onClick={() => setAiMode("auto")} data-testid="wa-ai-handback" className="px-2.5 py-1.5 bg-[#16A34A] text-white text-[10px] font-bold uppercase flex items-center gap-1 hover:bg-[#15803D]">
                      <Bot size={12} /> Serahkan ke AI
                    </button>
                  ) : (
                    <button onClick={() => setAiMode("manual")} data-testid="wa-ai-takeover" className="px-2.5 py-1.5 border border-[#E4E4E7] bg-white text-[10px] font-bold uppercase flex items-center gap-1 hover:bg-[#F4F4F5]">
                      <Hand size={12} /> Ambil Alih
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-[#FAFAFA]" data-testid="wa-thread-messages">
                {!thread ? (
                  <div className="text-center text-xs text-[#52525B] py-6">Memuat…</div>
                ) : thread.messages.length === 0 ? (
                  <div className="text-center text-xs text-[#52525B] py-6" data-testid="wa-thread-empty">Belum ada pesan. Tulis pesan pertama di bawah.</div>
                ) : thread.messages.map((m) => (
                  <div key={m.id} id={m.wa_key ? `wamsg-${m.wa_key}` : undefined} className={`group flex items-center gap-1 transition-colors ${m.from_me ? "justify-end" : "justify-start"}`}>
                    {m.from_me && (
                      <button
                        onClick={() => startReply(m)}
                        data-testid={`wa-msg-reply-${m.id}`}
                        title="Balas pesan ini"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-[#52525B] hover:bg-[#F4F4F5] border border-transparent hover:border-[#E4E4E7]"
                      >
                        <Reply size={13} />
                      </button>
                    )}
                    {m.from_me && (
                      <button
                        onClick={() => deleteMessage(m)}
                        data-testid={`wa-msg-delete-${m.id}`}
                        title="Hapus pesan"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 border border-transparent hover:border-[#DC2626]/20"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    <div onDoubleClick={() => startReply(m)} className={`max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.from_me ? "bg-[#DCF8C6] border border-[#c6ecb0]" : "bg-white border border-[#E4E4E7]"}`}>
                      {m.reply_to && (
                        <button
                          type="button"
                          data-testid={`wa-msg-quote-${m.id}`}
                          onClick={() => jumpToMessage(m.reply_to.key)}
                          className={`block w-full text-left mb-1.5 px-2 py-1 border-l-4 bg-black/5 hover:bg-black/10 ${m.reply_to.from_me ? "border-[#16A34A]" : "border-[#002FA7]"}`}
                        >
                          <div className={`text-[10px] font-bold ${m.reply_to.from_me ? "text-[#16A34A]" : "text-[#002FA7]"}`}>{m.reply_to.from_me ? "Anda" : (title || "Pelanggan")}</div>
                          <div className="text-xs text-[#52525B] line-clamp-2">{m.reply_to.body}</div>
                        </button>
                      )}
                      {m.media_file && (m.media_mime || "").startsWith("image/") && (
                        <img
                          data-testid={`wa-media-img-${m.id}`}
                          src={`${API}/wa/media/${m.media_file}`}
                          alt="media"
                          loading="lazy"
                          className="max-w-full w-64 border border-[#E4E4E7] mb-1.5 cursor-pointer bg-white"
                          onClick={() => window.open(`${API}/wa/media/${m.media_file}`, "_blank")}
                        />
                      )}
                      {m.media_file && (m.media_mime || "").startsWith("video/") && (
                        <video
                          data-testid={`wa-media-video-${m.id}`}
                          src={`${API}/wa/media/${m.media_file}`}
                          controls
                          playsInline
                          preload="metadata"
                          className="max-w-full w-64 max-h-80 border border-[#E4E4E7] mb-1.5 bg-black"
                        />
                      )}
                      {m.media_file && (m.media_mime || "").startsWith("audio/") && (
                        <audio
                          data-testid={`wa-media-audio-${m.id}`}
                          src={`${API}/wa/media/${m.media_file}`}
                          controls
                          preload="metadata"
                          className="w-64 max-w-full mb-1.5"
                        />
                      )}
                      {m.media_file && !/^(image|video|audio)\//.test(m.media_mime || "") && (
                        <a href={`${API}/wa/media/${m.media_file}`} target="_blank" rel="noreferrer" className="block text-[#002FA7] underline text-xs mb-1">📎 Buka / unduh file</a>
                      )}
                      {m.media_file && /^(video|audio)\//.test(m.media_mime || "") && (
                        <a href={`${API}/wa/media/${m.media_file}`} target="_blank" rel="noreferrer" className="block text-[#002FA7] underline text-[10px] mb-1">⬇ Unduh</a>
                      )}
                      {m.body}
                      <div className="text-[9px] text-[#A1A1AA] font-mono text-right mt-1">{m.ai ? "🤖 AI · " : ""}{fmtTime(m.ts)}{m.from_me ? " ✓" : ""}</div>
                    </div>
                    {!m.from_me && (
                      <button
                        onClick={() => startReply(m)}
                        data-testid={`wa-msg-reply-${m.id}`}
                        title="Balas pesan ini"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-[#52525B] hover:bg-[#F4F4F5] border border-transparent hover:border-[#E4E4E7]"
                      >
                        <Reply size={13} />
                      </button>
                    )}
                    {!m.from_me && (
                      <button
                        onClick={() => deleteMessage(m)}
                        data-testid={`wa-msg-delete-${m.id}`}
                        title="Hapus pesan dari inbox"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 border border-transparent hover:border-[#DC2626]/20"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {replyTo && (
                <div className="border-t border-[#E4E4E7] px-3 pt-2 flex items-start gap-2 bg-white" data-testid="wa-reply-preview">
                  <div className={`flex-1 min-w-0 border-l-4 bg-[#F4F4F5] px-2 py-1 ${replyTo.from_me ? "border-[#16A34A]" : "border-[#002FA7]"}`}>
                    <div className={`text-[10px] font-bold ${replyTo.from_me ? "text-[#16A34A]" : "text-[#002FA7]"}`}>Membalas {replyTo.from_me ? "pesan Anda" : (title || "pelanggan")}</div>
                    <div className="text-xs text-[#52525B] line-clamp-2 whitespace-pre-wrap break-words">{replyTo.body}</div>
                  </div>
                  <button onClick={() => setReplyTo(null)} title="Batal balas" data-testid="wa-reply-cancel" className="p-1 text-[#52525B] hover:bg-[#F4F4F5]">
                    <X size={14} />
                  </button>
                </div>
              )}
              <div className={`${replyTo ? "" : "border-t border-[#E4E4E7] "}p-3 flex items-end gap-2 relative`}>
                {scOpen && (
                  <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-[#E4E4E7] shadow-lg z-30 max-h-64 overflow-y-auto" data-testid="wa-shortcut-dropdown">
                    <div className="px-3 py-2 border-b border-[#E4E4E7] flex items-center gap-2 sticky top-0 bg-white">
                      <Zap size={12} className="text-[#16A34A]" />
                      <input
                        autoFocus
                        value={scQuery}
                        onChange={(e) => setScQuery(e.target.value)}
                        placeholder="Cari shortcut…"
                        className="flex-1 outline-none text-xs"
                        data-testid="wa-shortcut-search"
                      />
                      <button onClick={() => setScOpen(false)} className="text-[#52525B] text-xs">✕</button>
                    </div>
                    {shortcuts.filter((s) => !scQuery || s.name.toLowerCase().includes(scQuery.toLowerCase()) || s.message.toLowerCase().includes(scQuery.toLowerCase())).map((s) => (
                      <button
                        key={s.id}
                        data-testid={`wa-shortcut-option-${s.id}`}
                        onClick={() => {
                          const nm = (title || "").trim();
                          const isPhone = !nm || /^[+\d\s-]+$/.test(nm);
                          const msg = s.message.replaceAll("{nama}", isPhone ? "Kak" : nm);
                          setText((t) => (t ? t + " " + msg : msg));
                          setScOpen(false);
                          setScQuery("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[#F4F4F5] border-b border-[#E4E4E7]/60 last:border-b-0"
                      >
                        <div className="text-xs font-bold text-[#16A34A]">/{s.name}</div>
                        <div className="text-[11px] text-[#52525B] line-clamp-2 whitespace-pre-wrap">{s.message}</div>
                      </button>
                    ))}
                    {shortcuts.length === 0 && (
                      <div className="px-3 py-3 text-xs text-[#52525B]">Belum ada shortcut. Buat di menu <b>WhatsApp → Shortcut Pesan</b>.</div>
                    )}
                  </div>
                )}
                <input ref={fileRef} type="file" className="hidden" data-testid="wa-media-input" onChange={(e) => sendMedia(e.target.files?.[0])} />
                <button data-testid="wa-attach-btn" onClick={() => fileRef.current?.click()} disabled={sending} title="Kirim foto / file (teks di kotak jadi caption)" className="px-3 py-2.5 border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] disabled:opacity-50">
                  <Paperclip size={16} />
                </button>
                <button data-testid="wa-shortcut-btn" onClick={() => { setScOpen((v) => !v); setScQuery(""); }} disabled={sending} title="Sisipkan shortcut pesan" className={`px-3 py-2.5 border bg-white hover:bg-[#F4F4F5] disabled:opacity-50 ${scOpen ? "border-[#16A34A] text-[#16A34A]" : "border-[#E4E4E7]"}`}>
                  <Zap size={16} />
                </button>
                <textarea
                  ref={inputRef}
                  data-testid="wa-reply-input"
                  value={text}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "/") { setScOpen(true); setScQuery(""); setText(""); return; }
                    setText(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && replyTo) { setReplyTo(null); return; }
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  placeholder="Tulis balasan… (Enter kirim, ketik / untuk shortcut)"
                  rows={2}
                  className="flex-1 px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] resize-none"
                />
                <button data-testid="wa-reply-send" onClick={send} disabled={sending || !text.trim()} className="px-4 py-2.5 bg-[#16A34A] text-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#15803D] disabled:opacity-50">
                  <Send size={14} /> Kirim
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
